/**
 * Brands (business lines) of the platform: Immosurance and ImmoKredita (#183).
 *
 * One build serves both domains: at startup (main.ts -> initBrand) the page's
 * hostname is compared with environment.immokreditaDomain; on that domain the
 * ImmoKredita brand is used, everywhere else Immosurance. Components read the
 * result through getCurrentBrand() / BrandService.
 * Backend counterpart: aml-backend v3/app/services/brand.service.js.
 *
 * TODO(#183): ImmoKredita site URL and support email are placeholders until
 * the client confirms them.
 */

export type BrandKey = 'immosurance' | 'immokredita';

export interface BrandColors {
    primary: string;
    primaryMid: string;
    primaryLight: string;
    accent: string;
    accentLight: string;
}

export interface Brand {
    key: BrandKey;
    name: string;
    tagline: string;
    /** Domain of the login cookies shared with the backend. */
    cookieDomain: string;
    siteUrl: string;
    supportEmail: string;
    title: string;
    favicon: string;
    logos: {
        horizontal: string;
        vertical: string;
        mark: string;
        markWhite: string;
    };
    colors: BrandColors;
    /** Virtual assistant (webchat widget) key; without one environment.ChatbotAgencyKey is used. */
    chatbotAgencyKey?: string;
}

export const DEFAULT_BRAND: BrandKey = 'immosurance';

export const BRANDS: Record<BrandKey, Brand> = {
    immosurance: {
        key: 'immosurance',
        name: 'Immosurance',
        tagline: 'COMPLYING IN REAL ESTATE',
        cookieDomain: '.immosurance.net',
        // replaced by environment.amlSiteUrl / supportEmail in initBrand()
        siteUrl: 'https://immosurance.net',
        supportEmail: 'support@immosurance.net',
        title: 'AML - Immosurance',
        favicon: 'assets/favicons/favicon.ico',
        logos: {
            horizontal: 'assets/images/logos/horizontal-primary-web.png',
            vertical: 'assets/images/logos/vertical-primary-web.png',
            mark: 'assets/images/logos/brand-mark-primary-web.png',
            markWhite: 'assets/images/logos/brand-mark-white.png',
        },
        colors: {
            primary: '#1B3D7F',
            primaryMid: '#2a57a8',
            primaryLight: '#e8edf8',
            accent: '#C0292B',
            accentLight: '#fce8e8',
        },
    },
    immokredita: {
        key: 'immokredita',
        // spelling of the brand guidelines: lowercase i, capital K
        name: 'immoKredita',
        // tagline of the brand guidelines (shown under the signup watermark)
        tagline: 'COMPLYING IN MORTGAGE CREDIT',
        cookieDomain: '.immokredita.com',
        siteUrl: 'https://immokredita.com',
        supportEmail: 'support@immokredita.com',
        title: 'AML - immoKredita',
        // client logos v3 (yellow rooftop and line); vertical = the v3 shield above the v3 name;
        // no white version in use yet
        favicon: 'assets/images/logos/immokredita/favicon.png',
        logos: {
            horizontal: 'assets/images/logos/immokredita/horizontal.png',
            vertical: 'assets/images/logos/immokredita/vertical.png',
            mark: 'assets/images/logos/immokredita/mark.png',
            markWhite: 'assets/images/logos/immokredita/mark-white.png',
        },
        // brand guidelines palette, used by the signup / login / password / 2FA screens only;
        // the app itself keeps each office's own theme
        colors: {
            primary: '#1C3049', // Shield Navy
            primaryMid: '#384A60', // Shield Navy +12.5% tint (hover)
            primaryLight: '#E8ECF1', // Paper Blue
            accent: '#E0A526', // Signal Amber: never as text on white
            accentLight: '#FBF4E4', // Signal Amber +87.5% tint
        },
        // webchat key for my.immokredita.com and dev1.immokredita.com
        chatbotAgencyKey: 'myimmokreditacom-dev1immokreditacom-b43a73',
    },
};

/** Development only: ?brand=immokredita on localhost, kept for the browser tab. */
const DEV_OVERRIDE_PARAM = 'brand';
const DEV_OVERRIDE_STORAGE_KEY = 'brandOverride';

/** URL settings that point at this app's own server and move with the domain. */
const SAME_ORIGIN_URL_KEYS = ['appUrl', 'rootUrl', 'apiUrl', 'yiiUrl', 'frontendUrl', 'socketUrl'];

let currentBrand: Brand = BRANDS[DEFAULT_BRAND];

export function isBrandKey(key: any): key is BrandKey {
    return typeof key === 'string' && Object.prototype.hasOwnProperty.call(BRANDS, key);
}

/**
 * Value of a `brand` filter in a Mongo query sent to the API (#183): records
 * without a brand belong to the default brand, as in the backend's brandQuery().
 */
export function brandFilterValue(key: string): any {
    const brand = isBrandKey(key) ? key : DEFAULT_BRAND;
    return brand === DEFAULT_BRAND ? { $in: [brand, null, ''] } : brand;
}

/** Display name of a stored brand key; records without one are the default brand. */
export function brandName(key: string): string {
    return BRANDS[isBrandKey(key) ? key : DEFAULT_BRAND].name;
}

/** 'immokredita' when the page runs on environment.immokreditaDomain, else null. */
export function brandFromHost(hostname: string, immokreditaDomain: string): BrandKey | null {
    const host = (hostname || '').trim().toLowerCase();
    const domain = (immokreditaDomain || '').trim().toLowerCase();
    return host && domain && host === domain ? 'immokredita' : null;
}

function devOverride(search: string): BrandKey | null {
    try {
        const requested = (new URLSearchParams(search).get(DEV_OVERRIDE_PARAM) || '').toLowerCase();
        if (isBrandKey(requested)) {
            sessionStorage.setItem(DEV_OVERRIDE_STORAGE_KEY, requested);
            return requested;
        }
        const stored = sessionStorage.getItem(DEV_OVERRIDE_STORAGE_KEY);
        return isBrandKey(stored) ? stored : null;
    } catch (e) {
        return null;
    }
}

export function resolveBrandKey(location: Location, env: any): BrandKey {
    return (
        brandFromHost(location.hostname, env.immokreditaDomain) ||
        (!env.production ? devOverride(location.search) : null) ||
        DEFAULT_BRAND
    );
}

/**
 * Points the build's own URLs (my.immosurance.net/...) at the domain the app
 * was opened on (my.immokredita.com/...), where nginx proxies /v3 and /yiiapp
 * to the same backends. URLs on other hosts (CDN, S3, local API on :8080) stay.
 */
function rehostEnvironment(env: any, location: Location): void {
    let buildHost: string;
    try {
        buildHost = new URL(env.appUrl).host;
    } catch (e) {
        return;
    }
    if (!buildHost || buildHost === location.host) {
        return;
    }
    SAME_ORIGIN_URL_KEYS.forEach((key) => {
        if (typeof env[key] !== 'string') {
            return;
        }
        try {
            const url = new URL(env[key]);
            if (url.host === buildHost) {
                url.protocol = location.protocol;
                url.host = location.host;
                let rehosted = url.toString();
                // URL adds a slash to bare origins ('https://host' -> 'https://host/')
                if (!env[key].endsWith('/') && rehosted.endsWith('/')) {
                    rehosted = rehosted.slice(0, -1);
                }
                env[key] = rehosted;
            }
        } catch (e) {
            // not an absolute URL, leave it
        }
    });
}

function applyToDocument(brand: Brand): void {
    const root = document.documentElement;
    root.setAttribute('data-brand', brand.key);
    root.style.setProperty('--brand-primary', brand.colors.primary);
    root.style.setProperty('--brand-primary-mid', brand.colors.primaryMid);
    root.style.setProperty('--brand-primary-light', brand.colors.primaryLight);
    root.style.setProperty('--brand-accent', brand.colors.accent);
    root.style.setProperty('--brand-accent-light', brand.colors.accentLight);

    document.title = brand.title;
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) {
        icon.href = brand.favicon;
        const ext = brand.favicon.split('.').pop().toLowerCase();
        icon.type = ext === 'svg' ? 'image/svg+xml' : ext === 'png' ? 'image/png' : 'image/x-icon';
    }
}

/**
 * Resolves the brand of this page and adapts the environment and document to it.
 * Must run before the app bootstraps (main.ts).
 */
export function initBrand(env: any, location: Location = window.location): Brand {
    const key = resolveBrandKey(location, env);
    const brand: Brand = { ...BRANDS[key] };

    if (key === DEFAULT_BRAND) {
        // keep the per-environment values the build already has
        brand.siteUrl = env.amlSiteUrl || brand.siteUrl;
        brand.supportEmail = env.supportEmail || brand.supportEmail;
    } else {
        rehostEnvironment(env, location);
        env.amlSiteUrl = brand.siteUrl;
        env.supportEmail = brand.supportEmail;
    }

    currentBrand = brand;
    applyToDocument(brand);
    return brand;
}

export function getCurrentBrand(): Brand {
    return currentBrand;
}
